/**
 * Service for managing and persisting application UI state via Backend API.
 */
export class StateService {
    constructor() {
        this.STORAGE_KEY = 'transwasm_ui_state';
        this.API_URL = import.meta.env.VITE_API_URL || '/api';
        this.state = {
            eqCollapsed: true,
            visualizerCollapsed: false,
            playbackSpeed: 1.0,
            selectorViewMode: 'waveform',
            eqGains: new Array(8).fill(1.0), // 8-band EQ defaults
            eqPreset: 'flat',
            customEqPresets: {}, // { name: [db1, db2...], ... }
            lastFile: null, // { name, hash }
            playbackPosition: 0,
            hasPlayed: false,
            theme: 'dark',
            zoomLevel: 0.25, // default seconds per inch
            visualizerThreshold: 0.0,
            visualizerMode: 'hybrid',
            pitchInCents: 0,
            shortcutsCollapsed: false,
            fileStates: {} // { [hash]: { playbackPosition, eqGains, eqPreset, playbackSpeed, zoomLevel } }
        };
        // Local cache as fallback
        this.loadLocalState();
        this.hasSynced = false;
    }

    /**
     * Loads state from local storage (offline fallback/initial boot).
     */
    loadLocalState() {
        if (typeof localStorage === 'undefined') return;
        const saved = localStorage.getItem(this.STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                this.state = { ...this.state, ...parsed };
            } catch (e) {
                console.error("Failed to parse local state", e);
            }
        }
    }

    /**
     * Fetches state from the server.
     */
    async syncFromServer(token) {
        if (!token) return;
        try {
            const res = await fetch(`${this.API_URL}/user/state`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const serverState = await res.json();

                if (serverState && Object.keys(serverState).length > 0) {

                    // Smart Merge Logic with Timestamp Resolution
                    const mergedState = { ...this.state };

                    // 1. Handle lastFile collision
                    const localFile = this.state.lastFile;
                    const serverFile = serverState.lastFile;

                    let useServerFile = false;

                    if (serverFile && serverFile.hash) {
                        if (!localFile || !localFile.hash) {
                            // No local file, take server
                            useServerFile = true;
                        } else {
                            // Both exist, check timestamps
                            const localTime = localFile.timestamp || 0;
                            const serverTime = serverFile.timestamp || 0;

                            if (serverTime > localTime) {
                                useServerFile = true;
                            }
                        }
                    }

                    if (useServerFile) {
                        mergedState.lastFile = serverFile;
                        // Also adopt server's playback context if taking server file
                        if (serverState.playbackPosition !== undefined) mergedState.playbackPosition = serverState.playbackPosition;
                        if (serverState.zoomLevel !== undefined) mergedState.zoomLevel = serverState.zoomLevel;
                    }

                    // 2. Merge other keys (non-critical collisions)
                    const generalKeys = ['theme', 'eqCollapsed', 'visualizerCollapsed', 'shortcutsCollapsed', 'playbackSpeed', 'pitchInCents', 'visualizerThreshold', 'visualizerMode', 'eqPreset', 'customEqPresets'];
                    generalKeys.forEach(key => {
                        if (serverState[key] !== undefined) {
                            mergedState[key] = serverState[key];
                        }
                    });

                    // 3. Merge File States (Shallow merge to preserve local-only cache)
                    if (serverState.fileStates) {
                        mergedState.fileStates = { ...this.state.fileStates, ...serverState.fileStates };
                    }

                    this.state = mergedState;
                    if (typeof localStorage !== 'undefined') {
                        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.state));
                    }
                }
            }
        } catch (e) {
            console.warn("[StateService] Could not sync from server, using local state.", e);
        } finally {
            this.hasSynced = true;
        }
    }

    /**
     * Saves current state to local storage and syncs with server if token is provided.
     */
    async saveState(updates = {}, token = null) {
        this.state = { ...this.state, ...updates };
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.state));
        }

        if (token) {
            // Prevent overwriting server state if we haven't synced yet
            // This avoids race conditions where initialization logic triggers saves before we have the latest server state.
            if (!this.hasSynced) {
                console.warn('[StateService] Skipping server save - not yet synced.');
                return;
            }

            try {
                await fetch(`${this.API_URL}/user/state`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ state: this.state })
                });
            } catch (e) {
                console.error("[StateService] Failed to sync state to server", e);
            }
        }
    }

    /**
     * Saves file-specific state (position, EQ, etc.)
     */
    saveFileSpecificState(hash, fileState, token = null) {
        if (!hash) return;

        const fileStates = this.state.fileStates || {};
        fileStates[hash] = { ...(fileStates[hash] || {}), ...fileState };

        this.saveState({ fileStates }, token);
    }

    /**
     * Gets file-specific state.
     */
    getFileSpecificState(hash) {
        if (!hash || !this.state.fileStates) return null;
        return this.state.fileStates[hash];
    }

    /**
     * Resets state to defaults and clears local storage.
     */
    resetToDefaults() {
        this.state = {
            eqCollapsed: true,
            visualizerCollapsed: false,
            playbackSpeed: 1.0,
            selectorViewMode: 'waveform',
            eqGains: new Array(8).fill(1.0),
            eqPreset: 'flat',
            customEqPresets: {},
            lastFile: null,
            playbackPosition: 0,
            playbackPosition: 0,
            hasPlayed: false,
            visualizerThreshold: 0.0,
            visualizerMode: 'hybrid',
            pitchInCents: 0,
            shortcutsCollapsed: false,
            fileStates: {}
        };
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(this.STORAGE_KEY);
        }
    }

    /**
     * Returns a specific state value.
     */
    get(key) {
        return this.state[key];
    }
}

export const stateService = new StateService();
